/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Which theme a visitor gets. One rule, shared the staticRoutes.js way (plain
 * ESM, zero imports) by:
 *   1. src/contexts/ThemeContext.tsx — the live theme after hydration, the
 *      toggle, and the listener for the phone's own dark-mode switch;
 *   2. server/spaRenderer.ts — the `<html class>` the server emits so the
 *      first paint is right without waiting for a script;
 *   3. the inline boot script in index.html, which cannot import anything
 *      and therefore REPEATS the rule in six lines —
 *      src/__tests__/themeBootScript.test.ts runs that script against this
 *      module over the whole input matrix so the two cannot drift.
 *
 * The rule (2026-10-01):
 *   - an explicit choice — the toggle in the header — wins everywhere and is
 *     the ONLY thing ever persisted (localStorage + a cookie for the server);
 *   - without one, a PHONE (viewport under Tailwind's md, the same line
 *     useIsMobile draws) follows the device: dark when the phone prefers
 *     dark, light otherwise;
 *   - without one, everything wider stays dark — the brand default, and the
 *     OS setting is deliberately ignored there as it always was.
 *
 * Until 2026-10-01 every visitor was dark unless they toggled, and the
 * default itself was written to storage on every visit, so the stored
 * `theme=dark` of a returning visitor cannot be told from a choice. Hence a
 * NEW key for choices, and of the legacy key only `light` is honoured: under
 * the old code nothing but the toggle could produce it.
 */

/** localStorage key AND cookie name of an explicit choice. */
export const THEME_CHOICE_KEY = 'theme_choice';
/** The pre-2026-10 key: `light` was a choice, `dark` was (almost always) the default. */
export const LEGACY_THEME_KEY = 'theme';

/** "A phone": below Tailwind's md, like src/hooks/useIsMobile.ts. */
export const PHONE_QUERY = '(max-width: 767px)';
export const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

/** The browser-chrome colour per theme (`<meta name="theme-color">`): the page background. */
export const THEME_COLOR = Object.freeze({ dark: '#020617', light: '#ffffff' });

/**
 * @param {unknown} value
 * @returns {value is 'light' | 'dark'}
 */
export const isTheme = (value) => value === 'light' || value === 'dark';

/**
 * The explicit choice behind a (storage or cookie) pair, or null.
 * @param {unknown} choice value under THEME_CHOICE_KEY
 * @param {unknown} legacy value under LEGACY_THEME_KEY
 * @returns {'light' | 'dark' | null}
 */
export const explicitTheme = (choice, legacy) =>
  isTheme(choice) ? choice : legacy === 'light' ? 'light' : null;

/**
 * The theme without an explicit choice.
 * @param {{ phone: boolean, prefersDark: boolean }} device
 * @returns {'light' | 'dark'}
 */
export const defaultTheme = ({ phone, prefersDark }) =>
  phone ? (prefersDark ? 'dark' : 'light') : 'dark';

/**
 * @param {{ choice?: unknown, legacy?: unknown, phone: boolean, prefersDark: boolean }} input
 * @returns {'light' | 'dark'}
 */
export const resolveTheme = ({ choice, legacy, phone, prefersDark }) =>
  explicitTheme(choice, legacy) ?? defaultTheme({ phone, prefersDark });

/**
 * The server's reading of "a phone", from what every request carries: the
 * low-entropy client hint Chromium sends unasked (`Sec-CH-UA-Mobile: ?1`),
 * else the User-Agent's Mobile token (Safari on iPhone, Firefox on Android).
 * Tablets read as desktop here, as they do under the 768 px rule in the
 * browser (iPadOS Safari and Android Chrome on tablets carry no Mobile
 * token). The client's own rule still decides before first paint; this only
 * makes the HTML the server sends right for the common case, so a first-time
 * phone visitor never sees a dark frame flash to light.
 * @param {string | undefined} userAgent
 * @param {string | undefined} chMobile the `Sec-CH-UA-Mobile` header
 */
export const isPhoneUserAgent = (userAgent, chMobile) => {
  if (chMobile === '?1') return true;
  if (chMobile === '?0') return false;
  // "Mobi" without a closing boundary: the token is "Mobile" on Android
  // Chrome and Firefox and "Mobile/15E148" on iPhone Safari.
  return /\b(?:Mobi|iPhone|iPod|Windows Phone)/i.test(userAgent || '');
};
